import { resetTokens, setTokens } from '@/app/context/auth/authActionCreators/authActionCreators';
import { authReducer } from '@/app/context/auth/authReducer/authReducer';
import { permissionCache } from '@/features/auth/access-control/permissionCache';
import { useMutation } from '@/react-query/hooks/useMutation/useMutation';
import { LoginMutationResponse } from '@/services/auth/auth.types';
import { authStorage } from '@/services/storage/AuthStorage';
import { jwtDecode } from 'jwt-decode';
import { useEffect, useMemo, useReducer } from 'react';
import { toast } from 'sonner';
import { RefreshTokenMutationResponse } from '@/services/client/interceptors/responseInterceptors';

export function useAuth() {
  const [state, dispatch] = useReducer(authReducer, {
    accessToken: authStorage.accessToken,
    refreshToken: authStorage.refreshToken,
    expires: authStorage.expires,
    fullName: authStorage.fullName,
    email: authStorage.email,
    profileImage: authStorage.profileImage,
  });

  const { mutateAsync: login, isPending: isAuthenticating } = useMutation('loginMutation', {
    onSuccess: (res: LoginMutationResponse) => {
      dispatch(
        setTokens({
          accessToken: res.accessToken,
          refreshToken: res.refreshToken,
          expires: res.accessTokenExpiry,
          fullName: res.fullName,
          email: res.email,
          profileImage: res.profileImage,
        }),
      );
    },
    onError: (error) => {
      const errorMessage =
        (error.data && typeof error.data === 'object' && 'message' in error.data && error.data.message) ||
        'Login failed';
      toast.error(String(errorMessage));
      dispatch(resetTokens());
    },
  });

  const { mutateAsync: logout } = useMutation('logoutMutation', {
    onSettled: () => {
      permissionCache.clear();
      dispatch(resetTokens());
      window.location.href = '/login';
    },
  });

  // Check if token is valid and not expired
  const isTokenValid = useMemo(() => {
    if (!state.accessToken) return false;

    try {
      const decoded = jwtDecode<{ exp: number }>(state.accessToken);
      const currentTime = Date.now() / 1000;
      return decoded.exp > currentTime;
    } catch {
      return false;
    }
  }, [state.accessToken]);

  // Sync state with authStorage
  useEffect(() => {
    authStorage.accessToken = state.accessToken;
    authStorage.refreshToken = state.refreshToken;
    authStorage.expires = state.expires;
    authStorage.fullName = state.fullName;
    authStorage.email = state.email;
    authStorage.profileImage = state.profileImage;
  }, [state]);

  // Auto-logout if token becomes invalid and there is no refresh token
  useEffect(() => {
    if (state.accessToken && !isTokenValid && !state.refreshToken) {
      dispatch(resetTokens());
    }
  }, [state.accessToken, isTokenValid, state.refreshToken]);

  // Sync state when interceptor refreshes tokens
  useEffect(() => {
    const handleTokenRefreshed = (e: Event) => {
      const customEvent = e as CustomEvent<RefreshTokenMutationResponse>;
      dispatch(
        setTokens({
          accessToken: customEvent.detail.accessToken,
          refreshToken: customEvent.detail.refreshToken,
          expires: customEvent.detail.accessTokenExpiry,
          fullName: customEvent.detail.fullName,
          email: customEvent.detail.email,
          profileImage: customEvent.detail.profileImage,
        }),
      );
    };

    window.addEventListener('auth-token-refreshed', handleTokenRefreshed);
    return () => window.removeEventListener('auth-token-refreshed', handleTokenRefreshed);
  }, []);

  return useMemo(
    () => ({
      ...state,
      isAuthenticating,
      isAuthenticated: isTokenValid,
      login,
      logout,
    }),
    [state, isAuthenticating, isTokenValid, login, logout],
  );
}
